<template>
  <w-step :state="state" class="custom-fields-container">
    <w-expandable-text
      v-if="state.facilityCustomText"
      class="custom-text"
      :text="state.facilityCustomText"
    ></w-expandable-text>
    <div v-if="showCustomFields" class="custom-fields">
      <div
        v-for="fieldObj in state.activeCustomFieldsForSelectedAvailability"
        v-bind:key="fieldObj.id"
        v-bind:class="{
          'widget-contact-custom-field': true,
          'widget-contact-comment': fieldObj.field_type === 'checkbox',
        }"
      >
        <b-field
          v-if="fieldObj.field_type === 'checkbox' && fieldObj.options.length"
          v-bind:class="{
            'widget-contact-custom-field__checkbox': true,
            'widget-contact-custom-field__checkbox--checked':
              hasSelectedCheckboxOptions(fieldObj.id),
          }"
          style="display: block"
        >
          <div v-if="fieldObj.name.length" style="margin-bottom: 1rem">
            <span class="widget-contact-custom-field__name">
              {{ fieldObj.name }}
              <span
                v-if="fieldObj.is_required"
                class="widget-contact-custom-field__field-required"
                ><span aria-hidden="true"><w-asterisk /></span
              ></span>
            </span>
          </div>
          <div
            class="widget-custom-field-choice-options widget-contact-custom-field__checkbox-option"
          >
            <b-checkbox
              v-for="option in fieldObj.options"
              :key="option.id"
              v-model="state.formValues.customFields[fieldObj.id]"
              :native-value="option.id"
              v-bind:disabled="state.isSavingBooking"
            >
              <span class="widget-contact-custom-field__name">
                {{ option.name }}
                <!-- display checkbox as required if the customfield has no title that could be displayed as required -->
                <span
                  v-if="fieldObj.is_required && !fieldObj.name.length"
                  class="widget-contact-custom-field__field-required"
                  ><span aria-hidden="true"><w-asterisk /></span
                ></span>
              </span>
            </b-checkbox>
          </div>
        </b-field>
        <div v-else-if="fieldObj.field_type === 'text'">
          <div>
            <span class="widget-contact-custom-field__name">
              {{ fieldObj.name }}
              <span
                v-if="fieldObj.is_required"
                class="widget-contact-custom-field__field-required"
                ><span aria-hidden="true"><w-asterisk /></span
              ></span>
            </span>
          </div>
          <w-input
            v-model="state.formValues.customFields[fieldObj.id]"
            type="text"
            v-bind:required="fieldObj.is_required"
            v-bind:placeholder="txtInputPlaceHolder"
            v-bind:disabled="state.isSavingBooking"
            @keydown.native.enter="onCommentEnterPress"
          />
        </div>
        <div
          v-else-if="fieldObj.field_type === 'choice'"
          class="widget-custom-field-select"
        >
          <div>
            <span class="widget-contact-custom-field__name">
              {{ fieldObj.name }}
              <span
                v-if="fieldObj.is_required"
                class="widget-contact-custom-field__field-required"
                ><span aria-hidden="true"><w-asterisk /></span
              ></span>
            </span>
          </div>
          <div class="widget-custom-field-choice-options">
            <b-radio
              v-for="option in fieldObj.options"
              :key="option.name"
              :native-value="option.id"
              v-model="state.formValues.customFields[fieldObj.id]"
              :disabled="state.isSavingBooking"
              :required="fieldObj.is_required"
            >
              {{ option.name }}
            </b-radio>
          </div>
        </div>
        <div
          v-else-if="
            fieldObj.field_type === 'select' && fieldObj.options.length
          "
          class="widget-custom-field-select"
        >
          <div>
            {{ fieldObj.name }}
            <span
              v-if="fieldObj.is_required"
              class="widget-contact-custom-field__field-required"
              ><span aria-hidden="true"><w-asterisk /></span
            ></span>
          </div>
          <div
            v-if="
              fieldObj.is_required && getRemainingRequired(fieldObj.id) !== 0
            "
            class="widget-custom-field-select-text"
          >
            {{ getRemainingText(fieldObj.id) }}
          </div>
          <div v-for="option in fieldObj.options" :key="option.id">
            <span>{{ option.name }}</span>
            <w-number-stepper
              :value="getStepperValue(fieldObj.id, option.id)"
              :min="0"
              :max="option.num"
              :disabled="state.isSavingBooking"
              @input="setStepperValue(fieldObj.id, option.id, $event)"
            />
          </div>
        </div>
      </div>
    </div>
  </w-step>
</template>

<script>
import i18n from '../i18n';
import InputField from './common/FormInputField';
import ExpandableText from './common/ExpandableText';
import Step from './common/Step.vue';
import NumberStepper from './common/NumberStepper.vue';
import Asterisk from './icons/Asterisk.vue';

export default {
  props: ['state'],
  components: {
    'w-input': InputField,
    'w-expandable-text': ExpandableText,
    'w-step': Step,
    'w-number-stepper': NumberStepper,
    'w-asterisk': Asterisk,
  },
  data: () => {
    return {
      txtInputPlaceHolder: i18n.gettext('Add text'),
    };
  },
  computed: {
    showCustomFields: function () {
      return (
        this.state.activeCustomFieldsForSelectedAvailability.length > 0 &&
        !this.state.isAddingWaitlist
      );
    },
  },
  methods: {
    onCommentEnterPress: function (e) {
      e.stopPropagation();
      e.preventDefault();
    },
    hasSelectedCheckboxOptions(fieldId) {
      const fieldValue = this.state.formValues.customFields[fieldId];
      return Array.isArray(fieldValue) && fieldValue.length > 0;
    },
    getStepperValue(fieldId, optionId) {
      const fieldValue = this.state.formValues.customFields[fieldId];
      if (!fieldValue || typeof fieldValue !== 'object') {
        return 0;
      }
      return fieldValue[optionId] || 0;
    },
    setStepperValue(fieldId, optionId, value) {
      if (
        !this.state.formValues.customFields[fieldId] ||
        typeof this.state.formValues.customFields[fieldId] !== 'object'
      ) {
        this.$set(this.state.formValues.customFields, fieldId, {});
      }

      this.$set(this.state.formValues.customFields[fieldId], optionId, value);
    },
    getFieldTotal(fieldId) {
      const fieldValue = this.state.formValues.customFields[fieldId];
      if (!fieldValue || typeof fieldValue !== 'object') {
        return 0;
      }
      return Object.values(fieldValue).reduce(
        (sum, val) => sum + Number(val || 0),
        0
      );
    },
    getRemainingRequired(fieldId) {
      const total = this.getFieldTotal(fieldId);
      const groupSize = Number(this.state.formValues.num || 0);
      return groupSize - total;
    },
    getRemainingText(fieldId) {
      const remaining = this.getRemainingRequired(fieldId);
      const groupSize = this.state.formValues.num;

      if (remaining === 0) return null;

      if (remaining > 0) {
        if (remaining === 1) {
          return i18n.sprintf(
            i18n.gettext(
              'Please select 1 more item to match your group of %s.'
            ),
            groupSize
          );
        }
        return i18n.sprintf(
          i18n.gettext(
            'Please select %s more items to match your group of %s.'
          ),
          remaining,
          groupSize
        );
      }

      const excess = Math.abs(remaining);
      if (excess === 1) {
        return i18n.sprintf(
          i18n.gettext(
            'Please deselect 1 more item to match your group of %s.'
          ),
          groupSize
        );
      }
      return i18n.sprintf(
        i18n.gettext(
          'Please deselect %s more items to match your group of %s.'
        ),
        excess,
        groupSize
      );
    },
  },
};
</script>

<style lang="scss">
@import '../styles/_variables.scss';

.custom-fields-container {
  overflow-x: hidden;
  overflow-y: auto;

  padding: 0 1.5rem 2rem 1.5rem;
}

.custom-text {
  padding-bottom: 1rem;
}

.custom-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.widget-contact-custom-field {
  border: 0.5px solid var(--theme-font-color-opacity-5);
  border-radius: 10px;
  padding: 1rem;

  @at-root #{$broadway} & {
    box-shadow: 0 0 7px 4px var(--theme-accent-color-opacity-5);
    border: none;
    border-radius: 20px;
    margin-top: 5px;

    .input {
      border: none;
    }
  }

  @at-root #{$glass} & {
    background: var(--theme-background-color-opacity-5);
    border: none;

    .input {
      &::placeholder {
        color: var(--theme-background-color) !important;
      }
    }
  }
}

.widget-custom-field-select {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.widget-custom-field-choice-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  .radio {
    margin: initial !important;
  }
}

.widget-custom-field-select-text {
  color: var(--theme-accent-color);
  font-size: 0.65rem;
  margin-block: -0.75rem;
}

.widget-contact-custom-field__checkbox {
  &.widget-contact-custom-field__checkbox--checked {
    color: inherit;
  }
}

.widget-contact-custom-field__checkbox-option:not(:last-child) {
  padding-bottom: 0.75rem;
  line-height: 1;
}

.widget-contact-custom-field__name {
  display: inline-flex;
  align-items: center;

  .widget-contact-custom-field__field-required {
    margin-left: 0.25rem;
    line-height: 1;

    svg {
      vertical-align: middle;
    }
  }
}
</style>
